/**
 * One text-correction rule (KAN-856, founder-approved 2026-10-22).
 *
 * - WRITING (notes, reasons, messages, descriptions): multi-line box, sentence
 *   capitals, autocorrect + spellcheck ON. Return inserts a new line.
 *   -> `proseFieldProps`
 * - NAMES AND PLACES (person, company, street, city): one line, capital on each
 *   word, autocorrect + spellcheck OFF. -> `nameFieldProps`
 * - SUBJECT / DESCRIPTION LINES (email subject, line-item description): one
 *   line, sentence capitals, autocorrect + spellcheck ON. -> `lineFieldProps`
 * - EXACT VALUES (email, phone, money, numbers, codes, search, typed confirms,
 *   ZIP): no capitals, no autocorrect, no spellcheck, plus the matching
 *   `type` / `inputMode` set by the caller. -> `exactFieldProps`
 *
 * Spread these objects; never hand-type the attributes.
 */
export const proseFieldProps = Object.freeze({
  autoCapitalize: 'sentences',
  autoCorrect: 'on',
  spellCheck: true,
});

export const nameFieldProps = Object.freeze({
  autoCapitalize: 'words',
  autoCorrect: 'off',
  spellCheck: false,
});

export const lineFieldProps = Object.freeze({
  autoCapitalize: 'sentences',
  autoCorrect: 'on',
  spellCheck: true,
});

export const exactFieldProps = Object.freeze({
  autoCapitalize: 'none',
  autoCorrect: 'off',
  spellCheck: false,
});

/**
 * One-line writing boxes (reasons that print on invoices / land in audit rows):
 * the box wraps visually but the VALUE never holds a line break.
 * `collapseLineBreaks` turns any run of line breaks (pasted text) into a single
 * space; `singleLineEnterKeyDown` makes plain Enter insert nothing. Enter is
 * ignored while an IME is composing so candidate selection still works.
 */
export const collapseLineBreaks = (value) => String(value ?? '').replace(/(?:\r?\n)+/g, ' ');

export function singleLineEnterKeyDown(e) {
  if (e.key !== 'Enter' || e.nativeEvent?.isComposing) return;
  e.stopPropagation();
  if (!e.shiftKey) e.preventDefault();
}
